import { NavigateFunction } from "react-router-dom";
import { Dispatch, UnknownAction } from "redux";
import { environment } from "../configuration";
import { getNotificationApi } from "../lib/antdAppApi";
import { apiSlice } from "../store/api/apiSlice";
import { optimizeImage } from "./imageOptimizer";

/**
 * Maps the legacy `sliceArg` identifiers the entity forms pass to
 * `handleSubmitFormResponseManage` onto RTK Query tag types, so creates and
 * updates done through plain services still invalidate the list caches.
 * Unknown slices (e.g. "tiendaPaquete", which has no RTKQ endpoints yet) are
 * a safe no-op.
 */
type EntityTagType =
  | "User"
  | "Course"
  | "Project"
  | "Lesson"
  | "School"
  | "Program"
  | "Resource"
  | "Subject"
  | "Grade";

const SLICE_TO_TAG_TYPE: Record<string, EntityTagType> = {
  user: "User",
  course: "Course",
  project: "Project",
  lesson: "Lesson",
  lessons: "Lesson",
  school: "School",
  program: "Program",
  resource: "Resource",
  subject: "Subject",
  grade: "Grade",
};

export const invalidateEntityList = (
  dispatch: Dispatch<UnknownAction>,
  sliceArg: string
) => {
  const tagType = SLICE_TO_TAG_TYPE[sliceArg];
  if (!tagType) return;
  dispatch(apiSlice.util.invalidateTags([{ type: tagType, id: "LIST" }]));
};

/**
 * Extracts a readable description from a failed backend call, for axios
 * errors (`error.response.data`) and RTK Query errors (`error.data`) alike.
 * The backend body is `{ statusCode, message, error }` where `message` is an
 * ARRAY of strings for ValidationPipe 400s, and opaque 500s carry a
 * `correlationId` that support can grep in the server logs — so it is always
 * appended when present instead of being silently dropped.
 */
export const getApiErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data ?? error?.data;
  const rawMessage = data?.message;
  const message = Array.isArray(rawMessage)
    ? rawMessage.join(" · ")
    : typeof rawMessage === "string" && rawMessage.trim()
    ? rawMessage
    : "";
  const correlation = data?.correlationId ? ` (ref: ${data.correlationId})` : "";
  // "Internal server error" alone tells the operator nothing the fallback
  // doesn't already say in Spanish — keep the fallback, keep the ref.
  if (!message || message === "Internal server error") {
    return `${fallback}${correlation}`;
  }
  return `${fallback}\n${message}${correlation}`;
};

export const handleSubmitFormResponseManage = async (
  valuesInput: any,
  createFunction: (values: any) => Promise<any>,
  udpateFunction: (id: number, values: any) => Promise<any>,
  navigate: NavigateFunction,
  dispatch: Dispatch<UnknownAction>,
  sliceArg: string,
  createFunctionDispatchArg: string,
  updateFunctionDispatchArg: string,
  id?: number,
  customErrorHandler?: (error: any) => void
) => {
  dispatch({
    type: `${sliceArg}/setLoading`,
    payload: true,
  });
  
  try {
    if (id) {
      const response = await udpateFunction(id, valuesInput);

      if (response?.id) {
        dispatch({
          type: `${sliceArg}/${updateFunctionDispatchArg}`,
          payload: {
            id: id,
            data: response,
          },
        });
        invalidateEntityList(dispatch, sliceArg);
        getNotificationApi().success({
          message: "Éxito",
          description: "Actualización exitosa.",
          placement: "top",
        });
        navigate(-1);
      } else {
        getNotificationApi().error({
          message: "Error",
          description: "Ocurrió un error al actualizar.",
          placement: "top",
        });
      }
    } else {
      const response = await createFunction(valuesInput);

      if (response.id) {
        dispatch({
          type: `${sliceArg}/${createFunctionDispatchArg}`,
          payload: response,
        });
        invalidateEntityList(dispatch, sliceArg);
        getNotificationApi().success({
          message: "Éxito",
          description: "Creación exitosa.",
          placement: "top",
        });
        navigate(-1);
      } else {
        getNotificationApi().error({
          message: "Error",
          description: "Ocurrió un error al crear.",
          placement: "top",
        });
      }
    }
  } catch (error: any) {
    // Si hay un manejador de errores custom, usarlo
    if (customErrorHandler) {
      customErrorHandler(error);
    } else {
      // Fallback: mostrar el mensaje del backend cuando existe
      getNotificationApi().error({
        message: "Error",
        description: getApiErrorMessage(error, "Ocurrió un error inesperado."),
        placement: "top",
        duration: 5,
      });
    }
  } finally {
    dispatch({
      type: `${sliceArg}/setLoading`,
      payload: false,
    });
  }
};

//función para subir archivos a GCP
export const customUpload = async (
  options: any,
  folder?: string
): Promise<{ fileName: string; url: string }> => {
  const { file: originalFile, onProgress, onSuccess, onError } = options;

  // Optimize images before deciding the upload path (no-op for non-images)
  const file =
    originalFile instanceof File
      ? await optimizeImage(originalFile)
      : originalFile;

  const FILE_SIZE_THRESHOLD = 10 * 1024 * 1024; // 10MB

  if (file.size > FILE_SIZE_THRESHOLD) {
    // directUpload must use the optimized file's name/type
    return directUpload({ ...options, file }, folder); // ya maneja el progreso
  }

  // Para archivos pequeños: usar XMLHttpRequest para capturar progreso
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("file", file);
    if (folder) {
      formData.append("folder", folder);
    }

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${environment.apiUrl}/storage/upload`, true);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress({ percent });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const result = JSON.parse(xhr.responseText);
        getNotificationApi().success({
          type: "success",
          message: "Archivo subido correctamente",
        });
        onSuccess && onSuccess(result);
        resolve(result);
      } else {
        const error = new Error(`Error al subir el archivo: ${xhr.statusText}`);
        getNotificationApi().error({ type: "error", message: error.message });
        onError && onError(error);
        reject(error);
      }
    };

    xhr.onerror = () => {
      const error = new Error("Error de red al subir el archivo");
      getNotificationApi().error({ type: "error", message: error.message });
      onError && onError(error);
      reject(error);
    };

    xhr.send(formData);
  });
};

//nueva función para carga directa cuando el archivo es mayor a 10MB
export const directUpload = async (
  options: any,
  folder?: string
): Promise<{ fileName: string; url: string }> => {
  const { file, onProgress, onSuccess, onError } = options;

  try {
    //paso 1: Preparar la carga obteniendo una URL firmada
    const prepareResponse = await fetch(
      `${environment.apiUrl}/storage/prepare-upload`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          folder: folder,
        }),
      }
    );

    if (!prepareResponse.ok) {
      throw new Error(
        `Error al preparar la carga: ${prepareResponse.statusText}`
      );
    }

    //extraer toda la información necesaria aquí con los nombres correctos
    const responseData = await prepareResponse.json();
    console.log("Respuesta completa de prepare-upload:", responseData);

    //usar los nombres exactos de la respuesta
    const { signedUrl, fileName, url } = responseData;

    //verificar que los valores existan
    if (!signedUrl || !fileName || !url) {
      console.error("Respuesta incompleta:", responseData);
      throw new Error(
        "La respuesta del servidor no incluye todos los campos necesarios"
      );
    }

    //obtener la extensión del archivo original
    const extension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    //crear el fileName con la extensión
    const fileNameWithExtension = fileName + extension;

    //paso 2: Subir el archivo directamente a Google Cloud Storage
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl, true);
    xhr.setRequestHeader("Content-Type", file.type);

    //configurar el seguimiento del progreso
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress({ percent });
      }
    };

    return new Promise((resolve, reject) => {
      //configurar los manejadores de eventos
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          //usar el nombre con extensión y la URL correcta
          const result = {
            fileName: fileNameWithExtension,
            url: url, //usar 'url' en lugar de 'publicUrl'
          };

          console.log("Resultado final de directUpload:", result);

          getNotificationApi().success({
            type: "success",
            message: "Archivo subido correctamente",
          });

          if (onSuccess) {
            onSuccess(result);
          }

          resolve(result);
        } else {
          const error = new Error(
            `Error al subir el archivo: ${xhr.statusText}`
          );
          getNotificationApi().error({
            type: "error",
            message: "Error al subir el archivo",
          });
          if (onError) {
            onError(error);
          }
          reject(error);
        }
      };

      xhr.onerror = (error) => {
        getNotificationApi().error({
          type: "error",
          message: "Error al subir el archivo",
        });
        if (onError) {
          onError(error);
        }
        reject(error);
      };

      //enviar el archivo
      xhr.send(file);
    });
  } catch (error) {
    console.error("Error en directUpload:", error);
    getNotificationApi().error({
      type: "error",
      message: "Error al subir el archivo",
    });
    if (onError) {
      onError(error);
    }
    return {
      fileName: "",
      url: "",
    };
  }
};
//función para subir archivos de un juego desde un zip usando el flujo de 3 pasos
export const uploadGameArchive = async (
  options: any,
  platform?: string,
): Promise<{ gameEntryPoint: string }> => {
  const { file, onProgress, onSuccess, onError, setProcessing } = options;

  try {
    // Paso 1: Preparar upload (progreso 0-10%)
    if (onProgress) {
      onProgress({ percent: 5 });
    }

    const prepareResponse = await fetch(
      `${environment.apiUrl}/storage/prepare-game-upload`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileName: file.name,
        }),
      }
    );

    if (!prepareResponse.ok) {
      throw new Error(
        `Error al preparar la carga: ${prepareResponse.statusText}`
      );
    }

    const { signedUrl, tempPath } = await prepareResponse.json();

    if (!signedUrl || !tempPath) {
      throw new Error(
        "La respuesta del servidor no incluye todos los campos necesarios"
      );
    }

    if (onProgress) {
      onProgress({ percent: 10 });
    }

    // Paso 2: Subir archivo directamente a Google Cloud Storage (progreso 10-80%)
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl, true);
    xhr.setRequestHeader("Content-Type", "application/zip");

    return await new Promise((resolve, reject) => {
      // Configurar el seguimiento del progreso para la subida (10% a 80%)
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const uploadPercent = Math.round((event.loaded / event.total) * 70); // 70% del total
          const totalPercent = 10 + uploadPercent; // Empezar desde 10%
          onProgress({ percent: totalPercent });
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            // Paso 3: Procesar archivo (progreso 80-100%)
            if (onProgress) {
              onProgress({ percent: 85 });
            }

            if (setProcessing) {
              setProcessing(true);
            }

            const processResponse = await fetch(
              `${environment.apiUrl}/storage/process-game-archive`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  tempPath: tempPath,
                  originalFileName: file.name,
                  platform: platform,
                }),
              }
            );

            if (!processResponse.ok) {
              throw new Error(
                `Error al procesar el archivo: ${processResponse.statusText}`
              );
            }

            const result = await processResponse.json();

            if (onProgress) {
              onProgress({ percent: 100 });
            }

            if (setProcessing) {
              setProcessing(false);
            }

            onSuccess?.(result);

            getNotificationApi().success({
              message: result.message || "Juego subido correctamente",
            });

            resolve({ gameEntryPoint: result.gameEntryPoint });
          } catch (processError) {
            if (setProcessing) setProcessing(false);
            onError?.(processError);
            reject(processError);
          }
        } else {
          const error = new Error(`Error al subir: ${xhr.statusText}`);
          if (setProcessing) setProcessing(false);
          onError?.(error);
          reject(error);
        }
      };

      xhr.onerror = (error) => {
        if (setProcessing) setProcessing(false);
        onError?.(error);
        reject(error);
      };

      // Enviar el archivo
      xhr.send(file);
    });
  } catch (error) {
    if (setProcessing) setProcessing(false);
    onError?.(error);
    return { gameEntryPoint: "" };
  }
};
